import { customerOverrideForDrilldown } from '@src/components/app-viewer-sessions/utils';
import { EQueryIdType } from '../types';
import {
  useGetClientIdsByViewerId,
  useGetTimelineClientIds,
  useGetTimelineViewerIds,
  useGetViewerIdsByClientId,
} from '../utils/queryHooks';
import { USER_ID_FIELD, idOptions } from '../constants';
import { IDSelectorProps } from '@conviva/organism-components';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateTempDashboardByTitle,
  createTempDashboard,
  removeKey,
} from '@store/tempDashboard/actions';
import { useHistory } from 'react-router-dom';
import usePrevious from '@hooks/usePrevious';
import { getAppViewerModuleTempDashboardUrl } from '@src/components/common/Dashboard/utils/getTempDashboardUrl';
import { DASHBOARD_TYPE } from '@src/utils/constants';

export const useIds = ({
  id,
  viewerId,
  clientId,
  selectedTrendMetric,
  interval,
  customer_id,
  idType,
  handleIdSelectChanged,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedViewerId, setSelectedViewerId] = useState<string>(viewerId);
  const [selectedClientId, setSelectedClientId] = useState<string>(clientId);
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    setSelectedClientId(clientId);
    setSelectedViewerId(viewerId);
  }, [clientId, viewerId]);

  const { data: clientIdArrByViewer, isLoading: clientIdLoading } = useGetClientIdsByViewerId(
    viewerId,
    selectedTrendMetric,
    interval,
    customer_id,
  );
  const { data: viewerIdArrByClient, isLoading: viewerIdLoading } = useGetViewerIdsByClientId(
    viewerId,
    clientId,
    selectedTrendMetric,
    interval,
    customer_id,
  );
  const { data: clientIdArr } = useGetTimelineClientIds(viewerId, selectedTrendMetric, interval);
  const { data: viewerIdArr } = useGetTimelineViewerIds(
    viewerId,
    selectedTrendMetric,
    interval,
    customer_id,
  );

  const genDashBoardTitle = (id: string, type: EQueryIdType) => {
    if (type === EQueryIdType.userId) {
      return `User - ${id}`;
    }
    if (type === EQueryIdType.clientId) {
      return `Client - ${id}`;
    }
    return '';
  };
  let dashboardTitle = '';
  if (viewerId) {
    dashboardTitle = genDashBoardTitle(viewerId, EQueryIdType.userId);
  } else if (clientId) {
    dashboardTitle = genDashBoardTitle(clientId, EQueryIdType.clientId);
  }
  const prevDashboardTitle = usePrevious(dashboardTitle);

  const handleSubSelectChange = useCallback(
    (v: string): void => {
      if (id) {
        dispatch(removeKey(id));
      }
      const redirectHandler = ({ id: tabId }) => {
        history.push(getAppViewerModuleTempDashboardUrl({ viewerId, clientId: v }, tabId));
      };
      dispatch(
        createTempDashboard(
          DASHBOARD_TYPE.APP_VIEWER_MODULE,
          { viewerId, clientId: v },
          dashboardTitle,
          {
            redirectHandler,
          },
        ),
      );
      setSelectedClientId(v);
    },
    [dashboardTitle, dispatch, history, id, viewerId],
  );

  useEffect(() => {
    if (Array.isArray(clientIdArrByViewer) && clientIdArrByViewer.length > 0) {
      const firstClientId = clientIdArrByViewer[0]?.client_id;
      if (firstClientId && !clientId) {
        handleSubSelectChange(firstClientId);
        setSelectedClientId(firstClientId);
      }
    }
  }, [clientId, clientIdArrByViewer, handleSubSelectChange]);

  useEffect(() => {
    if (idType === EQueryIdType.userId) {
      setDeviceOptions(
        clientIdArrByViewer?.map((e) => ({
          label: e.device_name,
          value: e.client_id,
        })) ?? [],
      );
      return;
    } else if (idType === EQueryIdType.clientId) {
      setDeviceOptions(
        clientIdArr?.map((e) => ({
          label: e.device_name,
          value: e.client_id,
        })) ?? [],
      );
      return;
    }
  }, [clientIdArr, clientIdArrByViewer, idType]);

  const updateUrlAndTabName = (v: string, type: EQueryIdType): void => {
    const arr: string[] = window.location.href.split('?');
    let title = '';
    if (type === EQueryIdType.userId) {
      title = 'userId';
    } else if (type === EQueryIdType.clientId) {
      title = 'clientId';
    }
    const BASE_URL_INDEX = 0;
    const res = `${arr[BASE_URL_INDEX]}?${title}=${encodeURIComponent(v)}`;
    window.history.replaceState({}, document.title, res);
  };

  const handleMainSelectChange = (v: string): void => {
    let newViewerId = '';
    let newClientId = '';
    let newDashboardTitle = '';
    let setMainStateFunc = setSelectedViewerId;

    if (idType === EQueryIdType.userId) {
      newViewerId = v;
      newDashboardTitle = genDashBoardTitle(v, EQueryIdType.userId);
    } else if (idType === EQueryIdType.clientId) {
      newClientId = v;
      newDashboardTitle = genDashBoardTitle(v, EQueryIdType.clientId);
      setMainStateFunc = setSelectedClientId;
    }

    // eslint-disable-next-line no-use-before-define
    if (v && id !== 'user-timeline') {
      updateUrlAndTabName(v, idType);
    }

    dispatch(
      updateTempDashboardByTitle(prevDashboardTitle, newDashboardTitle, {
        viewerId: newViewerId,
        clientId: newClientId,
      }),
    );
    if (id) {
      dispatch(removeKey(id));
    }
    const redirectHandler = ({ id: tabId }) => {
      history.push(
        getAppViewerModuleTempDashboardUrl({ viewerId: newViewerId, clientId: newClientId }, tabId),
      );
    };
    dispatch(
      createTempDashboard(
        DASHBOARD_TYPE.APP_VIEWER_MODULE,
        { viewerId: newViewerId, clientId: newClientId },
        newDashboardTitle,
        {
          redirectHandler,
        },
      ),
    );
    setMainStateFunc(v);
  };

  const userIdOptions =
    viewerIdArr?.map((e: string) => ({
      label: e,
      value: e,
    })) ?? [];

  const clientIdOptions =
    clientIdArr?.map((e) => ({
      label: e.client_id,
      value: e.client_id,
    })) ?? [];

  const clientIdArrByViewerOptions =
    clientIdArrByViewer?.map((e) => ({
      label: e.client_id,
      value: e.client_id,
    })) ?? [];

  const filterOption = (
    input: string,
    option?: { label: string; value: string | number },
  ): boolean => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  const res: IDSelectorProps[] = [];
  if (idType === EQueryIdType.userId) {
    res.push(
      {
        titleValue: EQueryIdType.userId,
        titleOptions: idOptions,
        titleOnChange: handleIdSelectChanged,
        value: selectedViewerId,
        options: userIdOptions,
        onChange: handleMainSelectChange,
        filterOption,
      },
      {
        titleValue: 'Client ID',
        titleDisabled: true,
        value: selectedClientId,
        options: clientIdArrByViewerOptions,
        onChange: handleSubSelectChange,
        filterOption,
      },
      {
        titleValue: 'Device Name',
        titleDisabled: true,
        value: selectedClientId,
        options: deviceOptions,
        onChange: handleSubSelectChange,
        filterOption,
      },
    );
  } else if (idType === EQueryIdType.clientId) {
    let uid = 'N/A';
    if (viewerIdArrByClient && viewerIdArrByClient.length > 0) {
      const key = customerOverrideForDrilldown[customer_id] || USER_ID_FIELD;
      uid = viewerIdArrByClient.map((e) => e[key]).join(' / ');
    }
    res.push(
      {
        titleValue: EQueryIdType.clientId,
        titleOptions: idOptions,
        titleOnChange: handleIdSelectChanged,
        value: selectedClientId || viewerId,
        options: clientIdOptions,
        onChange: handleMainSelectChange,
        filterOption,
      },
      {
        titleValue: 'Device Name',
        titleDisabled: true,
        value: viewerIdArrByClient?.[0]?.device_name ?? 'N/A',
        disabled: true,
      },
      {
        titleValue: 'User ID',
        titleDisabled: true,
        value: uid,
        disabled: true,
      },
    );
  }

  return {
    selectedViewerId,
    selectedClientId,
    idGroupData: res,
    idLoading: clientIdLoading || viewerIdLoading,
    idType,
  };
};
